// ═══════════════════════════════════════════════════════════════════════════════
// PAGE - Paramètres Mentions Légales Devis
// Deux modes : Formulaire classique OU Configuration assistée par Abra
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileText, Wand2, Settings, Save, Loader2, Check, AlertCircle,
  MessageCircle, Send, Euro, Percent, Shield, Clock, ChevronRight,
  Sparkles, ArrowLeft, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ═══════════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════════

export default function MentionsLegalesPage() {
  const [mode, setMode] = useState(null); // null = choix, 'form' = formulaire, 'abra' = assistant
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  // Données du formulaire
  const [formData, setFormData] = useState({
    payment_terms: '',
    deposit_percent: 30,
    warranty_text: '',
    custom_footer: '',
    validity_days: 30
  });
  
  // État pour le mode Abra
  const [abraMessages, setAbraMessages] = useState([]);
  const [abraInput, setAbraInput] = useState('');
  const [abraStep, setAbraStep] = useState(0);
  const [isAbraThinking, setIsAbraThinking] = useState(false);
  const messagesEndRef = useRef(null);
  
  // Récupérer le token d'auth
  const getAuthToken = () => {
    return localStorage.getItem('social_token') || localStorage.getItem('auth_token');
  };
  
  // Charger les données existantes
  useEffect(() => {
    const fetchLegalData = async () => {
      try {
        const token = getAuthToken();
        if (!token) {
          setIsLoading(false);
          return;
        }
        
        const response = await fetch(`${API_URL}/api/cockpit/boutique/legal`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setFormData({
            payment_terms: data.payment_terms || '',
            deposit_percent: data.deposit_percent || 30,
            warranty_text: data.warranty_text || '',
            custom_footer: data.custom_footer || '',
            validity_days: data.validity_days || 30
          });
        }
      } catch (error) {
        console.error('Error loading legal data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchLegalData();
  }, []);
  
  // Scroll auto pour le chat Abra
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [abraMessages]);
  
  // Sauvegarder les données
  const handleSave = async () => {
    setIsSaving(true);
    try {
      const token = getAuthToken();
      if (!token) {
        toast.error('Tu dois être connecté pour sauvegarder');
        return;
      }
      
      const response = await fetch(`${API_URL}/api/cockpit/boutique/legal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        toast.success('Mentions légales enregistrées !');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };
  
  // ═══════════════════════════════════════════════════════════════════════════
  // MODE ABRA - Questions conversationnelles
  // ═══════════════════════════════════════════════════════════════════════════
  
  const abraQuestions = [
    {
      question: "Salut ! Je vais t'aider à configurer les mentions légales de tes devis. Commençons par l'acompte. Quel pourcentage d'acompte demandes-tu habituellement à tes clients ? (ex: 30%, 50%)",
      field: 'deposit_percent',
      parse: (input) => {
        const match = input.match(/(\d+)/);
        return match ? Math.min(100, Math.max(0, parseInt(match[1]))) : 30;
      },
      validate: (val) => val >= 0 && val <= 100,
      errorMsg: "Hmm, je n'ai pas compris. Donne-moi un pourcentage entre 0 et 100."
    },
    {
      question: "Parfait ! Et pour les conditions de paiement du solde, comment ça se passe chez toi ? (ex: 'à la fin des travaux', 'à 30 jours', '50% à mi-chantier et 50% à la fin')",
      field: 'payment_terms',
      parse: (input) => input.trim(),
      validate: (val) => val.length > 3,
      errorMsg: "Peux-tu me donner un peu plus de détails sur tes conditions de paiement ?"
    },
    {
      question: "Super ! Maintenant parlons garanties. Tu as une assurance décennale, une RC Pro ? Donne-moi les infos à afficher (ex: 'Garantie décennale AXA n°123456')",
      field: 'warranty_text',
      parse: (input) => input.trim(),
      validate: (val) => val.length > 5,
      errorMsg: "C'est important pour rassurer tes clients ! Précise au moins le type de garantie."
    },
    {
      question: "Presque fini ! As-tu des mentions spéciales à ajouter ? Labels (RGE, Qualibat...), régime TVA particulier, ou autre ? (Tu peux répondre 'non' si tu n'en as pas)",
      field: 'custom_footer',
      parse: (input) => {
        const lower = input.toLowerCase().trim();
        if (lower === 'non' || lower === 'rien' || lower === 'pas de mention') return '';
        return input.trim();
      },
      validate: () => true,
      errorMsg: ""
    },
    {
      question: "Dernière question : combien de jours de validité pour tes devis ? (ex: 30, 60, 90)",
      field: 'validity_days',
      parse: (input) => {
        const match = input.match(/(\d+)/);
        return match ? Math.min(365, Math.max(7, parseInt(match[1]))) : 30;
      },
      validate: (val) => val >= 7 && val <= 365,
      errorMsg: "La validité doit être entre 7 et 365 jours."
    }
  ];
  
  const startAbraMode = () => {
    setMode('abra');
    setAbraStep(0);
    setAbraMessages([
      { role: 'assistant', content: abraQuestions[0].question }
    ]);
  };
  
  const handleAbraSubmit = async () => {
    if (!abraInput.trim() || isAbraThinking) return;
    
    const userMessage = abraInput.trim();
    setAbraInput('');
    setAbraMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsAbraThinking(true);
    
    // Simuler un délai de réflexion
    await new Promise(resolve => setTimeout(resolve, 800));
    
    const currentQuestion = abraQuestions[abraStep];
    const parsedValue = currentQuestion.parse(userMessage);
    
    if (!currentQuestion.validate(parsedValue)) {
      setAbraMessages(prev => [...prev, { role: 'assistant', content: currentQuestion.errorMsg }]);
      setIsAbraThinking(false);
      return;
    }
    
    // Mettre à jour le formData
    setFormData(prev => ({
      ...prev,
      [currentQuestion.field]: parsedValue
    }));
    
    // Passer à la question suivante
    const nextStep = abraStep + 1;
    
    if (nextStep < abraQuestions.length) {
      setAbraStep(nextStep);
      setAbraMessages(prev => [...prev, { role: 'assistant', content: abraQuestions[nextStep].question }]);
    } else {
      // Terminé !
      setAbraMessages(prev => [...prev, {
        role: 'assistant',
        content: "C'est tout bon ! J'ai configuré tes mentions légales. Voici le résumé :",
        summary: true
      }]);
      
      // Sauvegarder automatiquement
      setTimeout(() => {
        handleSave();
      }, 1000);
    }
    
    setIsAbraThinking(false);
  };
  
  // ═══════════════════════════════════════════════════════════════════════════
  // RENDU - Choix du mode
  // ═══════════════════════════════════════════════════════════════════════════
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#1E3A5F]" />
      </div>
    );
  }
  
  if (mode === null) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-[#1E3A5F] to-[#3B82F6] rounded-2xl flex items-center justify-center mx-auto mb-4">
            <FileText className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Mentions légales devis</h1>
          <p className="text-gray-600">Configure les informations qui apparaîtront sur tous tes devis PDF</p>
        </div>
        
        <div className="grid md:grid-cols-2 gap-4">
          {/* Mode Formulaire */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setMode('form')}
            className="p-6 bg-white border-2 border-gray-200 rounded-2xl text-left hover:border-[#1E3A5F] hover:shadow-lg transition-all group"
          >
            <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#1E3A5F]/10 transition-colors">
              <Settings className="w-6 h-6 text-gray-600 group-hover:text-[#1E3A5F]" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Mode formulaire</h3>
            <p className="text-sm text-gray-500">
              Remplis les champs toi-même, à ton rythme. Idéal si tu sais exactement ce que tu veux.
            </p>
            <div className="flex items-center gap-1 text-[#1E3A5F] text-sm font-medium mt-4">
              Commencer <ChevronRight className="w-4 h-4" />
            </div>
          </motion.button>
          
          {/* Mode Assistant */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={startAbraMode}
            className="p-6 bg-gradient-to-br from-[#1E3A5F] to-[#2d5a8a] rounded-2xl text-left text-white hover:shadow-xl transition-all relative overflow-hidden group"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
            
            <div className="relative">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4 backdrop-blur-sm">
                <Wand2 className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
                Mode Assistant
                <Sparkles className="w-4 h-4 text-amber-300" />
              </h3>
              <p className="text-sm text-white/80">
                Je te pose les questions et je remplis tout pour toi. Simple et rapide !
              </p>
              <div className="flex items-center gap-1 text-white text-sm font-medium mt-4">
                Lancer l'assistant <ChevronRight className="w-4 h-4" />
              </div>
            </div>
          </motion.button>
        </div>
        
        {/* Aperçu des données actuelles */}
        {(formData.payment_terms || formData.warranty_text) && (
          <div className="mt-8 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">Configuration existante détectée</p>
                <p className="text-sm text-amber-700 mt-1">
                  Tu as déjà des mentions légales configurées. Tu peux les modifier avec l'un des deux modes.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
  
  // ═══════════════════════════════════════════════════════════════════════════
  // RENDU - Mode Formulaire
  // ═══════════════════════════════════════════════════════════════════════════
  
  if (mode === 'form') {
    return (
      <div className="max-w-2xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => setMode(null)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Mentions légales devis</h1>
            <p className="text-sm text-gray-500">Mode formulaire</p>
          </div>
        </div>
        
        <div className="space-y-6">
          {/* Acompte */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <Percent className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Acompte</h3>
                <p className="text-sm text-gray-500">Pourcentage demandé à la commande</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min="0"
                max="100"
                step="5"
                value={formData.deposit_percent}
                onChange={(e) => setFormData(prev => ({ ...prev, deposit_percent: parseInt(e.target.value) }))}
                className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-emerald-500"
              />
              <div className="w-20 flex items-center justify-center px-3 py-2 bg-emerald-50 rounded-lg">
                <span className="text-lg font-bold text-emerald-600">{formData.deposit_percent}%</span>
              </div>
            </div>
          </div>
          
          {/* Conditions de paiement */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Euro className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Conditions de paiement</h3>
                <p className="text-sm text-gray-500">Comment et quand le solde doit être payé</p>
              </div>
            </div>
            <textarea
              value={formData.payment_terms}
              onChange={(e) => setFormData(prev => ({ ...prev, payment_terms: e.target.value }))}
              placeholder="Ex: Solde à la fin des travaux, à 30 jours de la facturation..."
              rows={2}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 resize-none"
            />
          </div>
          
          {/* Garantie */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Garanties & Assurances</h3>
                <p className="text-sm text-gray-500">Décennale, RC Pro, numéros de contrat...</p>
              </div>
            </div>
            <textarea
              value={formData.warranty_text}
              onChange={(e) => setFormData(prev => ({ ...prev, warranty_text: e.target.value }))}
              placeholder="Ex: Garantie décennale AXA n°123456789 - RC Pro MAAF n°987654321"
              rows={2}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 resize-none"
            />
          </div>
          
          {/* Validité */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Validité du devis</h3>
                <p className="text-sm text-gray-500">Nombre de jours avant expiration</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={formData.validity_days}
                onChange={(e) => setFormData(prev => ({ ...prev, validity_days: parseInt(e.target.value) }))}
                className="flex-1 px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 bg-white"
              >
                <option value={15}>15 jours</option>
                <option value={30}>30 jours</option>
                <option value={45}>45 jours</option>
                <option value={60}>60 jours</option>
                <option value={90}>90 jours</option>
              </select>
            </div>
          </div>
          
          {/* Mentions personnalisées */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Mentions personnalisées</h3>
                <p className="text-sm text-gray-500">Labels, certifications, régime TVA...</p>
              </div>
            </div>
            <textarea
              value={formData.custom_footer}
              onChange={(e) => setFormData(prev => ({ ...prev, custom_footer: e.target.value }))}
              placeholder="Ex: Entreprise certifiée RGE QualiPAC. TVA non applicable, art. 293B du CGI."
              rows={3}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500/20 focus:border-gray-500 resize-none"
            />
          </div>
          
          {/* Bouton Sauvegarder */}
          <div className="flex gap-3">
            <button
              onClick={() => setMode('abra')}
              className="flex items-center gap-2 px-5 py-3 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Wand2 className="w-5 h-5" />
              Passer en mode Assistant
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex-1 flex items-center justify-center gap-2 py-3 bg-[#1E3A5F] text-white rounded-xl hover:bg-[#2d5a8a] transition-colors disabled:opacity-50"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  Enregistrer
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // ═══════════════════════════════════════════════════════════════════════════
  // RENDU - Mode Assistant (Chat)
  // ═══════════════════════════════════════════════════════════════════════════
  
  if (mode === 'abra') {
    const isComplete = abraStep >= abraQuestions.length;
    
    return (
      <div className="max-w-2xl mx-auto p-6 h-[calc(100vh-200px)] flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={() => setMode(null)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#1E3A5F] to-[#3B82F6] rounded-xl flex items-center justify-center">
              <Wand2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Ton Assistant</h1>
              <p className="text-xs text-gray-500">Configuration assistée</p>
            </div>
          </div>
          {!isComplete && (
            <div className="ml-auto text-sm text-gray-500">
              Question {abraStep + 1} / {abraQuestions.length}
            </div>
          )}
        </div>
        
        {/* Progress bar */}
        <div className="h-1 bg-gray-200 rounded-full mb-4 overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-[#1E3A5F] to-[#3B82F6]"
            initial={{ width: 0 }}
            animate={{ width: `${((abraStep + (isComplete ? 1 : 0)) / abraQuestions.length) * 100}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>
        
        {/* Chat messages */}
        <div className="flex-1 overflow-y-auto space-y-4 mb-4">
          <AnimatePresence>
            {abraMessages.map((msg, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] px-4 py-3 rounded-2xl ${
                    msg.role === 'user'
                      ? 'bg-[#1E3A5F] text-white rounded-br-md'
                      : 'bg-gray-100 text-gray-800 rounded-bl-md'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  
                  {/* Résumé final */}
                  {msg.summary && (
                    <div className="mt-4 p-3 bg-white rounded-xl border border-gray-200 text-gray-800">
                      <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Récapitulatif</p>
                      <ul className="space-y-1 text-sm">
                        <li className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-emerald-500" />
                          Acompte : <strong>{formData.deposit_percent}%</strong>
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-emerald-500" />
                          Paiement : <strong>{formData.payment_terms || '—'}</strong>
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-emerald-500" />
                          Garantie : <strong>{formData.warranty_text || '—'}</strong>
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-emerald-500" />
                          Validité : <strong>{formData.validity_days} jours</strong>
                        </li>
                        {formData.custom_footer && (
                          <li className="flex items-center gap-2">
                            <Check className="w-4 h-4 text-emerald-500" />
                            Mentions : <strong>{formData.custom_footer}</strong>
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
          
          {/* Indicateur de réflexion */}
          {isAbraThinking && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex justify-start"
            >
              <div className="px-4 py-3 bg-gray-100 rounded-2xl rounded-bl-md">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </motion.div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
        
        {/* Input ou boutons finaux */}
        {isComplete ? (
          <div className="flex gap-3">
            <button
              onClick={() => {
                setAbraStep(0);
                setAbraMessages([{ role: 'assistant', content: abraQuestions[0].question }]);
              }}
              className="flex items-center gap-2 px-5 py-3 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <RefreshCw className="w-5 h-5" />
              Recommencer
            </button>
            <button
              onClick={() => setMode('form')}
              className="flex-1 flex items-center justify-center gap-2 py-3 bg-[#1E3A5F] text-white rounded-xl hover:bg-[#2d5a8a] transition-colors"
            >
              <Settings className="w-5 h-5" />
              Voir en mode formulaire
            </button>
          </div>
        ) : (
          <div className="flex gap-2">
            <input
              type="text"
              value={abraInput}
              onChange={(e) => setAbraInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAbraSubmit()}
              placeholder="Ta réponse..."
              className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20 focus:border-[#1E3A5F]"
              disabled={isAbraThinking}
            />
            <button
              onClick={handleAbraSubmit}
              disabled={!abraInput.trim() || isAbraThinking}
              className="px-4 py-3 bg-[#1E3A5F] text-white rounded-xl hover:bg-[#2d5a8a] transition-colors disabled:opacity-50"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>
    );
  }
  
  return null;
}
